import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, CheckCircle2, RotateCcw, Loader2, Store } from "lucide-react";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/format";

/**
 * Master/super_user dialog to review sales that staff have soft-deleted.
 * Each row can be:
 *   - VERIFIED   (lock in the deletion — sale stays hidden forever)
 *   - RESTORED   (un-delete — stock is re-deducted, sale comes back to listings)
 *
 * Bulk "Verify All" clears the entire queue with one click.
 */
export default function PendingDeletionsDialog({ open, onOpenChange, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null); // sale id currently mutating
  const [confirmVerifyAll, setConfirmVerifyAll] = useState(false);
  const [confirmRestore, setConfirmRestore] = useState(null); // sale obj awaiting restore

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/sales/pending-deletions");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const handleVerify = async (sale) => {
    setBusyId(sale.id);
    try {
      await api.post(`/sales/${sale.id}/verify-deletion`);
      toast.success(`Deletion verified · ${sale.invoice_number || sale.id.slice(0, 8)}`);
      setItems((arr) => arr.filter((s) => s.id !== sale.id));
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const handleRestore = async (sale) => {
    setBusyId(sale.id);
    try {
      await api.post(`/sales/${sale.id}/restore-deletion`);
      toast.success(`Sale restored · ${sale.invoice_number || sale.id.slice(0, 8)}`);
      setItems((arr) => arr.filter((s) => s.id !== sale.id));
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
      setConfirmRestore(null);
    }
  };

  const handleVerifyAll = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/sales/verify-all-deletions");
      toast.success(`Verified ${data.verified} pending deletions`);
      setItems([]);
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
      setConfirmVerifyAll(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className="max-w-4xl max-h-[85vh] flex flex-col"
          data-testid="pending-deletions-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading tracking-tight">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
              Sales Awaiting Deletion Review
            </DialogTitle>
            <DialogDescription>
              These sales were deleted by staff and are <strong>hidden from your daily reports</strong>,
              but the records are kept until you (the owner) verify or restore them.
              <span className="block mt-1 text-xs">
                <strong>Verify</strong> = lock in the deletion forever ·{" "}
                <strong>Restore</strong> = un-delete (stock will be re-deducted).
              </span>
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-auto -mx-6 px-6">
            {loading && items.length === 0 ? (
              <div className="py-16 flex items-center justify-center text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-sm text-muted-foreground">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500 opacity-70" />
                Nothing to review — all sale deletions are settled.
              </div>
            ) : (
              <table className="w-full text-sm" data-testid="pending-deletions-table">
                <thead className="sticky top-0 bg-[hsl(var(--muted))] z-10">
                  <tr className="text-left">
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Sale
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Shop
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Customer
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">
                      Items
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">
                      Total
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium">
                      Deleted By · When
                    </th>
                    <th className="p-2 text-xs uppercase tracking-wider text-muted-foreground font-medium text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const isBusy = busyId === s.id;
                    return (
                      <tr
                        key={s.id}
                        className="border-t border-[hsl(var(--border))] data-row"
                        data-testid={`pending-deletion-row-${s.id}`}
                      >
                        <td className="p-2 font-mono-data text-xs">
                          {s.invoice_number || s.id.slice(0, 8)}
                          {s.type === "wholesale" && (
                            <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-blue-50 text-blue-700">
                              W
                            </span>
                          )}
                        </td>
                        <td className="p-2 text-xs">
                          <span className="inline-flex items-center gap-1">
                            <Store className="w-3 h-3 text-muted-foreground" />
                            {s.shop_name || "—"}
                          </span>
                        </td>
                        <td className="p-2 text-xs">{s.customer_name}</td>
                        <td className="p-2 text-right font-mono-data text-xs">{s.items_count}</td>
                        <td className="p-2 text-right font-mono-data text-sm font-semibold">
                          ₹{Number(s.total).toFixed(2)}
                        </td>
                        <td className="p-2 text-xs text-muted-foreground">
                          <div className="font-medium text-slate-700">{s.deleted_by_name}</div>
                          <div className="font-mono-data text-[10px]">{formatDateTime(s.deleted_at)}</div>
                        </td>
                        <td className="p-2 text-right whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs mr-1"
                            disabled={isBusy}
                            onClick={() => setConfirmRestore(s)}
                            data-testid={`pending-deletion-restore-${s.id}`}
                            title="Un-delete this sale (stock will be re-deducted)"
                          >
                            <RotateCcw className="w-3 h-3 mr-1" />
                            Restore
                          </Button>
                          <Button
                            size="sm"
                            className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                            disabled={isBusy}
                            onClick={() => handleVerify(s)}
                            data-testid={`pending-deletion-verify-${s.id}`}
                            title="Lock in the deletion permanently"
                          >
                            {isBusy ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                            )}
                            Verify
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          <DialogFooter className="flex justify-between items-center gap-2 sm:justify-between border-t border-[hsl(var(--border))] pt-3 mt-2">
            <div className="text-xs text-muted-foreground">
              {items.length > 0 && (
                <span data-testid="pending-deletions-count">
                  {items.length} sale{items.length === 1 ? "" : "s"} pending
                </span>
              )}
            </div>
            <div className="flex gap-2">
              {items.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmVerifyAll(true)}
                  disabled={loading}
                  data-testid="pending-deletions-verify-all-btn"
                  className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Verify All
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                data-testid="pending-deletions-close-btn"
              >
                Close
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Verify All */}
      <AlertDialog open={confirmVerifyAll} onOpenChange={setConfirmVerifyAll}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Verify all {items.length} pending deletions?</AlertDialogTitle>
            <AlertDialogDescription>
              This locks in every staff-initiated sale deletion. The sales will stay hidden permanently —
              you won't be able to restore them after this. The audit trail (who/when) is preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="verify-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleVerifyAll}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="verify-all-confirm"
            >
              Verify All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Restore */}
      <AlertDialog open={!!confirmRestore} onOpenChange={(o) => !o && setConfirmRestore(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Restore {confirmRestore?.invoice_number || "this sale"}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The sale will be un-deleted and re-appear in your daily reports. Stock will be re-deducted
              automatically. If any item is now out of stock, the restore will fail and the sale stays
              in pending-deletion state.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="restore-deletion-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleRestore(confirmRestore)}
              data-testid="restore-deletion-confirm"
            >
              Restore Sale
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
